import styled, { createGlobalStyle, css } from "styled-components";
import gradient1 from "./img/gradient1.png";
import gradient5 from "./img/gradient5.png";
import PPEikoLight from "./font/PPEiko-Light.woff2";
import PPEikoRegular from "./font/PPEiko-Regular.woff2";
import PPEikoLightItalic from "./font/PPEiko-LightItalic.woff2";

// ============================================================================
// FONT DEFINITIONS
// ============================================================================

export const PPEikoLightFont = createGlobalStyle`
  @font-face {
    font-family: "PPEikoLight";
    src: url(${PPEikoLight}) format("woff2");
  }
`;

export const PPEikoRegularFont = createGlobalStyle`
  @font-face {
    font-family: "PPEikoRegular";
    src: url(${PPEikoRegular}) format("woff2");
  }
`;

export const PPEikoLightItalicFont = createGlobalStyle`
  @font-face {
    font-family: "PPEikoLightItalic";
    src: url(${PPEikoLightItalic}) format("woff2");
  }
`;

// ============================================================================
// COMMON STYLES & MIXINS
// ============================================================================

const buttonTextStyles = css`
  text-align: center;
  font-family: "Gotham Rounded Medium";
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 120%;
  text-transform: uppercase;
`;

const commonTextStyles = css`
  color: #fff;
  font-family: "Gotham Rounded Light";
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%;
`;

const commonHeadingStyles = css`
  color: #fff;
  font-family: "Gotham Rounded Light";
  font-style: normal;
  font-weight: 300;
  line-height: 130%;
`;

const glassmorphismBackground = css`
  background: linear-gradient(
    152deg,
    rgba(0, 0, 0, 0.3) -12.8%,
    rgba(0, 0, 0, 0.03) 107.88%
  );
  backdrop-filter: blur(50px);
`;

const gradientBackground = (gradient, position = "top 20% left 0%") => css`
  background-color: #000;
  background-image: url(${gradient});
  background-size: cover;
  background-position: ${position};
  background-repeat: no-repeat;
`;

const flexCenter = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const flexColumn = css`
  display: flex;
  flex-direction: column;
`;

// ============================================================================
// LAYOUT COMPONENTS
// ============================================================================

export const MainContainer = styled.div`
  ${flexColumn}
  padding: 16px 0;
  width: 100%;
  max-width: 480px;
  margin: auto;
  min-height: 100vh;
`;

export const Navbar = styled.div`
  ${flexCenter}
  justify-content: space-between;
  padding: 16px 24px;
  background-color: #000;

  .balance-container {
    ${flexColumn}
    gap: 4px;

    p {
      color: #fff;
      font-family: "Gotham Rounded Medium";
      font-size: 14px;
      font-weight: 500;
      line-height: normal;
      text-transform: uppercase;
    }
  }
`;

export const LanguageSelector = styled.div`
  height: 60px;
  padding: 10px 22px;
  display: flex;
  justify-content: flex-end;
  cursor: pointer;

  span {
    color: white;
  }

  img {
    margin-left: 8px;
    width: 16px;
    height: 16px;
  }
`;

// ============================================================================
// HERO SECTIONS
// ============================================================================

export const LogoSection = styled.div`
  ${flexCenter}
  background-color: #000;
  padding: 24px 24px 32px;

  img {
    width: 248px;
    height: 100%;
  }
`;

export const IntroContainer = styled.div`
  ${flexColumn}
  gap: 32px;
  width: 100%;
  padding: 64px 24px 284px;
  ${gradientBackground(gradient1)}

  .header-container {
    ${flexColumn}
    align-items: center;
    gap: 16px;
  }

  h1,
  .header-text {
    color: white;
    font-family: "PPEikoRegular";
    font-size: 40px;
    text-align: center;
    font-weight: 500;
    line-height: 100%;
    text-transform: uppercase;
    margin: 0;
  }

  p {
    ${commonTextStyles}
    text-align: center;
    text-transform: uppercase;
  }
`;

export const ExploreSection = styled.div`
  ${flexColumn}
  gap: 32px;
  width: 100%;
  padding: 64px 24px 32px;
  background-color: #000;

  img {
    margin-top: -300px;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  h2 {
    ${commonHeadingStyles}
    font-size: 32px;
    margin: 0;
  }

  .italic-text {
    font-family: "PPEikoLightItalic";
  }

  p {
    ${commonTextStyles}
  }
`;

// ============================================================================
// BUTTON COMPONENTS
// ============================================================================

export const ButtonBlack = styled.div`
  ${flexColumn}
  gap: 32px;
  width: 100%;
  padding: 16px 32px;
  background-color: #000;
  color: #fff;
  ${buttonTextStyles}
  border: 1px solid white;
  border-radius: 14px;
  cursor: pointer;
`;

export const ButtonWhite = styled.div`
  ${flexColumn}
  gap: 32px;
  width: 100%;
  padding: 16px 32px;
  background-color: #fff;
  color: #000;
  ${buttonTextStyles}
  border-radius: 14px;
  cursor: pointer;
`;

// ============================================================================
// SCHEDULE SECTION
// ============================================================================

export const ScheduleSection = styled.div`
  ${flexColumn}
  width: 100%;
  padding: 64px 0px 120px;
  ${gradientBackground(gradient1, "top -30% left 0%")}

  h3 {
    text-align: center;
    font-family: "Gotham Rounded Medium";
    color: #fff;
    font-size: 12px;
    font-weight: 500;
    line-height: 130%;
    text-transform: uppercase;
  }

  h2 {
    text-align: center;
    font-family: "PPEikoLightItalic";
    font-size: 24px;
    font-weight: 300;
    line-height: 130%;
    color: #fff;
  }
`;

export const ScheduleInnerContainer = styled.div`
  ${flexColumn}
  width: 100%;
  border-radius: 32px;
  padding: 0 24px;
  ${glassmorphismBackground}
`;

export const DaysButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: 8px;
  justify-content: space-between;
  align-items: center;
  padding: 0 16px;
  margin: 32px 0 24px;
`;

export const DayButton = styled.div`
  padding: 10px 18px;
  height: 40px;
  ${flexCenter}
  background-color: ${({ active }) => (active ? "#fff" : "transparent")};
  color: ${({ active }) => (active ? "#000" : "#fff")};
  border-radius: 1000px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  ${buttonTextStyles}
  cursor: pointer;
  font-family: "Gotham Rounded Light";
`;

// ============================================================================
// CAROUSEL COMPONENTS
// ============================================================================

export const CarouselContainer = styled.div`
  ${flexColumn}
  width: 100%;

  .slide-image {
    width: 100%;
    height: 100%;
    max-width: 340px;
    margin: 0 auto;
    object-fit: cover;
    opacity: 0;
    transition: opacity 0.7s cubic-bezier(0.4, 0, 0.2, 1);

    &.visible {
      opacity: 1;
    }
  }
`;

export const ProgressBarContainer = styled.div`
  width: 100px;
  height: 4px;
  background: rgba(30, 30, 30, 0.5);
  border-radius: 3px;
  overflow: hidden;
  margin: 24px auto 120px;
`;

export const ProgressBar = styled.div`
  height: 100%;
  background: #fff;
  transition: width 0.2s linear;
`;

// ============================================================================
// SCHEDULE CONTENT
// ============================================================================

export const ScheduleContainer = styled.div`
  ${flexColumn}
  width: 100%;
  padding: 0;
  gap: 24px;
  border-radius: 32px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  overflow: hidden;
  position: relative;
`;

export const ScheduleScrollArea = styled.div`
  ${flexColumn}
  width: 100%;
  max-height: 400px;
  overflow-y: auto;
  padding: 32px 32px 32px 32px;

  /* Custom scrollbar styles */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
    margin-top: 16px;
    margin-bottom: 16px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
  }

  /* Firefox scrollbar */
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.3) rgba(255, 255, 255, 0.1);

  .event-container {
    ${flexColumn}
    gap: 8px;
    margin-top: 16px;
  }

  .date {
    color: #fff;
    font-family: "Gotham Rounded Medium";
    font-size: 18px;
    font-weight: 400;
    line-height: 130%;
    text-transform: uppercase;
  }

  .time {
    color: #fff;
    font-family: "Gotham Rounded Light";
    font-size: 16px;
    font-weight: 400;
    line-height: 130%;
    text-transform: uppercase;
  }

  .details {
    color: #fff;
    font-family: "Gotham Rounded Light";
    font-size: 16px;
    font-weight: 400;
    line-height: 130%;
    text-transform: uppercase;
    opacity: 0.7;
  }

  .title {
    color: #fff;
    font-family: "PPEikoLightItalic";
    font-size: 18px;
    font-weight: 300;
    line-height: 130%;
  }
`;

// ============================================================================
// JOURNEY SECTION
// ============================================================================

export const YourJourneySection = styled.div`
  margin-top: -50px;
  ${flexColumn}
  width: 100%;
  padding: 32px 0 240px;
  border-top-left-radius: 32px;
  border-top-right-radius: 32px;
  ${gradientBackground(gradient1, "top 0% left 0%")}
`;

export const YourJourneyContainer = styled.div`
  ${flexColumn}
  width: 100%;
  padding: 32px;
  justify-content: center;
  align-items: center;
  /* border: 1px solid rgba(255, 255, 255, 0.4); */
  border-radius: 32px;
  background: #000;
  gap: 16px;

  .header-container {
    width: 100%;
    ${flexColumn}
    gap: 8px;
  }

  h2 {
    ${commonHeadingStyles}
    font-size: 24px;
    align-self: flex-start;
  }

  .italic-text {
    font-family: "PPEikoLightItalic";
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  p {
    ${commonTextStyles}
  }
`;

// ============================================================================
// BOBO ACCOUNT SECTION
// ============================================================================

export const BoboAccountSection = styled.div`
  margin-top: -50px;
  ${flexColumn}
  width: 100%;
  padding: 32px 27px 160px;
  gap: 12px;
  ${gradientBackground(gradient5, "top 100% left 0%")}
  border-radius: 32px;

  .header-container {
    ${flexColumn}
    gap: 8px;
  }

  img {
    width: 100%;
    height: auto;
    object-fit: contain;
    margin: auto;
  }

  img[alt="your account"] {
    margin: -150px auto 40px;
    max-width: 340px;
  }
  img[alt="wearable"] {
    max-width: 315px;
  }

  h2 {
    ${commonHeadingStyles}
    font-size: 24px;
    align-self: flex-start;
  }

  .italic-text {
    font-family: "PPEikoLightItalic";
  }

  p {
    ${commonTextStyles}
  }

  .info-text {
    text-align: center;
    text-transform: uppercase;
    font-family: "Gotham Rounded Medium";
    font-size: 14px;
    font-weight: 500;
    line-height: 120%;
    margin-bottom: 16px;
  }

  .activation-code-value {
    background-color: #fff;
    border-radius: 14px;
    height: 40px;
    padding: 16px 32px;
    margin-bottom: 24px;
    color: #000;
    font-family: "Gotham Rounded Medium";
    font-size: 16px;
    font-weight: 500;
    line-height: 120%;
    text-transform: uppercase;
    ${flexCenter}
    width: 100%;
  }

  .app-store-container {
    display: flex;
    flex-direction: row;
    gap: 12px;
    justify-content: center;
    align-items: center;

    img {
      width: auto;
      max-width: 140px;
      height: 48px;
      object-fit: contain;
      cursor: pointer;
    }
  }
`;

// ============================================================================
// ADDITIONAL INFO SECTION
// ============================================================================

export const AdditionalInfoSection = styled.div`
  ${flexColumn}
  margin-top: -50px;
  z-index: -1;
  width: 100%;
  padding: 110px 24px 64px;
  height: 386px;
  gap: 12px;
  ${gradientBackground(gradient5, "top 70% left 0%")}
`;

export const AdditionalInfoContent = styled.div`
  margin-top: -280px;
  margin-bottom: 70px;
  margin-left: 24px;
  margin-right: 24px;

  .inner-container {
    ${flexColumn}
    gap: 32px;
    border-radius: 32px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: linear-gradient(
      143deg,
      rgba(0, 0, 0, 0.3) 21.63%,
      rgba(0, 0, 0, 0.03) 113.8%
    );
    backdrop-filter: blur(80px);
    padding: 24px;
  }

  h2 {
    ${commonHeadingStyles}
    font-size: 24px;
    text-align: center;
  }

  .italic-text {
    font-family: "PPEikoLightItalic";
  }

  .button {
    cursor: pointer;
    display: flex;
    height: 56px;
    padding: 16px 32px;
    justify-content: center;
    align-items: center;
    gap: 12px;
    align-self: stretch;
    border-radius: 14px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    background: #fff;
    color: #000;
    font-family: "Gotham Rounded Medium";
    font-size: 14px;
  }
`;

// ============================================================================
// FOOTER SECTION
// ============================================================================

export const FooterSection = styled.div`
  ${flexColumn}
  width: 100%;
  padding: 40px 22px;
  background-color: #000;
  gap: 24px;

  .social-container {
    display: flex;
    flex-direction: row;
    gap: 16px;
    justify-content: center;
    align-items: center;

    img {
      width: 56px;
      height: 56px;
      object-fit: contain;
      cursor: pointer;
    }
  }

  .footer-text-container {
    ${flexColumn}
    gap: 12px;
    justify-content: center;
    align-items: center;

    p {
      color: #fff;
      font-family: "Gotham Rounded Light";
      font-size: 14px;
      text-align: center;
    }
  }

  img[alt="footer logo"] {
    width: auto;
    height: 64px;
    max-width: 240px;
    margin: 0 auto;
    object-fit: contain;
  }
`;
